import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import html2pdf from "html2pdf.js";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "../../../../../api/hooks/useAPI";
import { aiDashboardKeys } from "../../../../../api/queryKeys";
import ContainedButton from "../../../../../components/buttons/ContainedButton";
import { EmptyState } from "../../../../../components/common/EmptyState";
import LoadingIcon from "../../../../../components/LoadingIcon";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { LATEST } from "../../../../../utils/constants";
import { useMCP } from "../../../../settings/organization/integrations/useMCP";
import {
  emptyStateIcons,
  icons,
} from "../../../../stack/environment/icons/iconExport";
import {
  createPdfConfig,
  createPdfContainer,
  createPdfHeader,
  measureContentForPdf,
} from "../../../custom_dashboard/utils/commonWidgetUtils";
import WidgetWrapper from "../../../custom_dashboard/WidgetWrapper";
import TopMCPServersChart from "./TopMCPServersChart";
import ViewSources from "./ViewSources";

const TopMCPServersWidget = forwardRef(
  ({ registerWidgetExport, widgetId }, ref) => {
    const navigate = useNavigate();
    const widgetRef = useRef(null);
    const { getTopMCPServersTrend, trackEvent } = useAPI();
    const [selectedPlatform, setSelectedPlatform] = useState({});
    const [openSources, setOpenSources] = useState(false);
    const [anchorElSources, setAnchorElSources] = useState(null);
    const { anyMCPIntegrationRegistered } = useMCP();
    const { data: topMCPServersTrend, isLoading: isLoadingTopMCPServers } =
      useQuery({
        queryKey: aiDashboardKeys.widgetType("topMCPServers"),
        queryFn: getTopMCPServersTrend,
        select: (data) => data.data,
      });

    const currentTimePoint =
      !topMCPServersTrend || Object.keys(topMCPServersTrend).length === 0
        ? null
        : topMCPServersTrend[LATEST];

    const allMcpServersData =
      !currentTimePoint || typeof currentTimePoint !== "object"
        ? []
        : Object.values(currentTimePoint).map((server) => ({
            display_name: server.display_name,
            affected_entities_count: server.affected_entities || 0,
            platform: server.platform,
            misconfiguration_id: server.misconfiguration_id,
            control_id: server.control_id,
            source: server.source,
          }));

    const availablePlatforms = Array.from(
      new Set(
        allMcpServersData.map((server) => server.platform).filter(Boolean)
      )
    ).sort();

    const isAllPlatformsSelected =
      Object.keys(selectedPlatform).length === 0 ||
      Object.values(selectedPlatform).every((value) => value === true);

    const mcpServersData = isAllPlatformsSelected
      ? allMcpServersData
      : allMcpServersData.filter(
          (server) => selectedPlatform[server.platform] === true
        );

    const uniqueControlIdsAndSources = Object.values(
      allMcpServersData.reduce((acc, server) => {
        acc[server.control_id] = {
          control_id: server.control_id,
          source: server.source,
        };
        return acc;
      }, {})
    );

    const platformFilterMenuItems = {
      title: "Select OS",
      key: "select-os",
      disabled: false,
      onclick: () => {},
      trailingIcon: icons.chevronRightIcon,
      submenuTitle: "Select OS",
      submenuMultiple: true,
      submenu: [
        ...availablePlatforms.map((platform) => ({
          title: platform,
          key: platform,
          selected: selectedPlatform[platform],
          onclick: () => {
            trackEvent("User clicked on platform filter option: " + platform);
            setSelectedPlatform({
              ...selectedPlatform,
              [platform]: !selectedPlatform[platform],
            });
          },
        })),
      ],
    };

    const handleViewSourcesClick = (event) => {
      setAnchorElSources(event.currentTarget);
      setOpenSources(true);
      trackEvent("User clicked on View Sources in MCP Servers widget");
    };

    const exportToPdf = async (isCombinedExport = false) => {
      if (!widgetRef.current) return null;

      if (isCombinedExport) {
        const widgetContainer = document.createElement("div");

        const widgetHeader = createPdfHeader(
          "Top MCP Servers by Affected Entities"
        );
        widgetContainer.appendChild(widgetHeader);

        const widgetClone = widgetRef.current.cloneNode(true);
        widgetClone.style.cssText = `
            background-color: white;
            padding: 24px;
            border: none;
            width: 89%;
            min-height: fit-content;
          `;

        const chartElement = widgetClone.querySelector(".apexcharts-canvas");
        if (chartElement) {
          chartElement.style.cssText = `
              width: 89% !important;
              height: 350px !important;
            `;
        }

        widgetContainer.appendChild(widgetClone);

        return widgetContainer;
      } else {
        const entirePageElement = await createPdfContainer();

        const header = createPdfHeader(
          "Top MCP Servers by Affected Entities",
          "AI Posture Dashboard"
        );
        entirePageElement.appendChild(header);

        const widgetClone = widgetRef.current.cloneNode(true);
        widgetClone.style.cssText = `
            background-color: white;
            padding: 24px;
            border: none;
            width: 89%;
            min-height: fit-content;
          `;

        const chartElement = widgetClone.querySelector(".apexcharts-canvas");
        if (chartElement) {
          chartElement.style.cssText = `
              width: 89% !important;
              height: 350px !important;
            `;
        }

        entirePageElement.appendChild(widgetClone);

        const { contentWidth, contentHeight } = await measureContentForPdf(
          entirePageElement
        );

        const pdfConfig = createPdfConfig(
          "Top_MCP_Servers_Widget.pdf",
          contentWidth,
          contentHeight
        );

        await html2pdf().set(pdfConfig).from(entirePageElement).save();
        return null;
      }
    };

    useImperativeHandle(ref, () => ({
      exportToPdf,
      hasExportCapability: true,
    }));

    useEffect(() => {
      if (registerWidgetExport && widgetId) {
        registerWidgetExport(widgetId, exportToPdf);
      }
    }, [registerWidgetExport, widgetId]);

    const handleSetUpMCPMonitoring = () => {
      navigate("/settings?category=organization_settings&page=mcp");
      trackEvent(
        "User clicked on Set up MCP monitoring button in MCP Servers widget"
      );
    };
    return (
      <WidgetWrapper
        title="Top MCP Servers by Affected Entities"
        info="Shows the top 5 MCP servers by number of impacts. Counts include duplicates, an entity can be impacted by multiple servers"
        subTitle={
          isAllPlatformsSelected
            ? "All Operating systems"
            : Object.keys(selectedPlatform)
                .filter((platform) => selectedPlatform[platform])
                .join(", ")
        }
        sortMenuItems={{
          data: [platformFilterMenuItems],
          tooltip: "Filter by operating system",
        }}
        viewAll={{
          handleViewAll: handleViewSourcesClick,
          tooltip: "View related controls",
          newTab: false,
          title: "View Sources",
          hideIcon: true,
        }}
      >
        <Box
          ref={widgetRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          {isLoadingTopMCPServers ? (
            <Box
              sx={{ display: "flex", justifyContent: "center", height: "100%" }}
            >
              <LoadingIcon size={10} />
            </Box>
          ) : !currentTimePoint || mcpServersData.length === 0 ? (
            <Box
              sx={{ display: "flex", justifyContent: "center", height: "100%" }}
            >
              {anyMCPIntegrationRegistered ? (
                <EmptyState
                  icon={emptyStateIcons.EmptyResultsIcon}
                  title="Monitoring enabled - results on the way"
                  description="We'll show MCP server impacts here after tonight's scan completes."
                  smallText={true}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "24px",
                  }}
                >
                  <EmptyState
                    icon={emptyStateIcons.EmptyResultsIcon}
                    title="No MCP monitoring detected"
                    description="Enable MCP monitoring in your MDM to discover and track MCP servers."
                    smallText={true}
                  />
                  <ContainedButton
                    onClick={handleSetUpMCPMonitoring}
                    color="primary"
                    size="small"
                    endIcon={
                      <SvgToIcon
                        IconLogo={icons.NewTabIcon}
                        width="20px"
                        height="20px"
                      />
                    }
                  >
                    Set up MCP monitoring
                  </ContainedButton>
                </Box>
              )}
            </Box>
          ) : (
            <>
              <Box sx={{ padding: "20px" }}>
                <TopMCPServersChart mcpServersData={mcpServersData} />
              </Box>
            </>
          )}
        </Box>
        <ViewSources
          open={openSources}
          onClose={() => setOpenSources(false)}
          anchorEl={anchorElSources}
          control_ids_and_sources={uniqueControlIdsAndSources}
        />
      </WidgetWrapper>
    );
  }
);

TopMCPServersWidget.displayName = "TopMCPServersWidget";

export default TopMCPServersWidget;
